/**
 * tradingViewConnect — the model behind the TradingView connect wizard (SUPERBETA-8).
 *
 * No React, no transport. Everything the wizard decides — what the member
 * copies, whether a strategy counts as connected, which of the three step-3
 * states to paint — is a pure function here so it can be tested without a DOM
 * and so the wizard itself stays presentational.
 *
 * WHAT IS RATIFIED, AND WHAT IS NOT (engine contract, services/signalsApi.js):
 *
 *   RATIFIED  POST /enroll → { webhookUrl, webhookToken, messageTemplate }
 *             One URL per member, idempotent per wallet, the credential already
 *             embedded in the URL. This is what step 2 hands over.
 *   RATIFIED  GET /status → recentSignals:[{ts,pair,intent,status}]
 *             The engine's own record of signals it received. This — and only
 *             this — is what turns step 3 green. The wizard never reports a
 *             connection the engine hasn't confirmed.
 *   NOT RATIFIED  a per-strategy webhook URL. The engine has no route that
 *             names the strategy in the URL, so one URL cannot by itself say
 *             which strategy an alert is for; that lives in the alert message
 *             the engine already hands us at /enroll. `carriesStrategy` is the
 *             single flag the UI branches on. When the engine ships
 *             per-strategy URLs this returns carriesStrategy: true, the alert
 *             message row disappears, and nothing else in the wizard changes.
 *
 * COMPLIANCE: nothing here produces copy about returns, income or outcomes. A
 * connected strategy means signals are reaching the engine — never that money
 * will be made.
 */

import { normalizePairKey } from "../../services/engineApi";

/** How long step 3 waits before offering the troubleshoot list. */
export const WAIT_TIMEOUT_MS = 10 * 60 * 1000;

/** How often step 3 asks the engine whether a signal has landed. */
export const CONNECT_POLL_MS = 8000;

/* ── signal history ────────────────────────────────────────────────────────
   /status.recentSignals is the engine's list, not ours: we read it, we never
   synthesize an entry. `ts` is whatever the engine sent — epoch ms today, but
   an ISO string parses too rather than silently becoming NaN and stranding a
   member on "waiting" forever. */

/** One signal's timestamp in epoch ms, or null when it isn't readable. */
export const signalMs = (sig) => {
  const raw = sig?.ts;
  if (raw == null) return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  const parsed = Date.parse(String(raw));
  return Number.isNaN(parsed) ? null : parsed;
};

/** Every signal the engine has recorded for one strategy. */
export const signalsForPair = (status, pair) => {
  const key = normalizePairKey(pair);
  if (!key) return [];
  const all = Array.isArray(status?.recentSignals) ? status.recentSignals : [];
  return all.filter((s) => normalizePairKey(s?.pair) === key);
};

/**
 * When the engine last saw a signal for this strategy, in epoch ms.
 * null ⇒ never, or the engine hasn't answered yet. Signals whose timestamp is
 * unreadable still count as "seen" (0), because the engine listing a signal is
 * itself the fact — the timestamp is only how we order them.
 */
export const lastSignalMs = (status, pair) => {
  const sigs = signalsForPair(status, pair);
  if (!sigs.length) return null;
  return sigs.reduce((max, s) => Math.max(max, signalMs(s) ?? 0), 0);
};

/** Has the engine ever received a signal for this strategy? */
export const isPairConnected = (status, pair) => lastSignalMs(status, pair) !== null;

/**
 * The normalized keys of every strategy the engine has received a signal for.
 * Rows use this for their connected indicator; an empty set is the tab's
 * empty-state CTA condition.
 */
export const connectedPairKeys = (status) => {
  const all = Array.isArray(status?.recentSignals) ? status.recentSignals : [];
  const out = new Set();
  all.forEach((s) => {
    const key = normalizePairKey(s?.pair);
    if (key) out.add(key);
  });
  return out;
};

/* ── what the member copies ───────────────────────────────────────────────── */

/**
 * Step 2's payload. `ready` false ⇒ the member isn't enrolled yet (no wallet
 * connected, or the engine hasn't answered /enroll), and step 2 says so rather
 * than rendering an empty box with a copy button that copies nothing.
 *
 * @param {{webhookUrl?:string, messageTemplate?:string}|null} enrollment
 * @param {string} pair
 * @returns {{ready:boolean, url:string, carriesStrategy:boolean, alertMessage:string}}
 */
export const strategyWebhook = (enrollment, pair) => {
  const url = typeof enrollment?.webhookUrl === "string" ? enrollment.webhookUrl : "";
  const alertMessage =
    typeof enrollment?.messageTemplate === "string" ? enrollment.messageTemplate : "";
  return {
    ready: Boolean(url),
    url,
    /* See the module note: not ratified, so honestly false. The wizard shows
       the alert message alongside precisely because this is false — dropping it
       would ship a step 2 that looks finished and doesn't work. */
    carriesStrategy: false,
    alertMessage,
    pairLabel: pair || "",
  };
};

/* ── step 3 ───────────────────────────────────────────────────────────────── */

/**
 * Which of step 3's three states to paint.
 *
 *   "connected"  the engine has recorded a signal for this strategy that it had
 *                not recorded when the wizard opened — proof this member's
 *                paste works, not just that they were connected once before
 *   "quiet"      nothing has arrived and the wait is over: the troubleshoot
 *                list, which is a list of things to check and never an error
 *   "waiting"    still listening
 *
 * `baselineMs` is the newest signal at the moment the wizard opened (null ⇒
 * none). Comparing against it is what stops a months-old signal from painting a
 * green check over a paste that never landed.
 */
export const connectPhase = ({ lastMs, baselineMs, elapsedMs }) => {
  const seen = lastMs != null && (baselineMs == null || lastMs > baselineMs);
  if (seen) return "connected";
  return Number(elapsedMs) >= WAIT_TIMEOUT_MS ? "quiet" : "waiting";
};

/* Three, never more: a long list at the moment something isn't working reads as
   a fault report. Ordered by how often each one is the actual cause. */
export const TROUBLESHOOT_STEPS = [
  "In TradingView, check the alert is switched on — a saved alert can still be paused.",
  "Make sure you pasted into the alert's Webhook URL box, and that TradingView's " +
    '"Webhook URL" notification option is ticked.',
  "TradingView sends webhooks on paid plans only. On a free plan the alert saves " +
    "but never sends.",
];

/** Where this strategy trades, in words a member can read. */
export const venueLabel = (venue) =>
  venue === "brokerage" ? "Brokerage — coming online in beta" : "gTrade · Arbitrum One";

export const TRADINGVIEW_URL = "https://www.tradingview.com/chart/";
